import React, { useCallback, useEffect, useRef, useState } from 'react';
import shopperPayoutService from '../../services/shopperPayoutService';
import Pagination from './Pagination';
import StatusBadge from './StatusBadge';

const PAGE_SIZE = 20;

const TABS = [
  { key: 'all', label: 'All' },
  { key: 'valid', label: 'Valid' },
  { key: 'invalid', label: 'Invalid' },
];

/**
 * "Shopper Payout Result" block: summary cards, All/Valid/Invalid tabs and the
 * paginated row table. Shared by the import wizard and the import detail page.
 *
 * Values are rendered exactly as they came out of the uploaded CSV, so an admin
 * can match a rejected row back to the line in their file.
 */
const PayoutRowsTable = ({
  batchId,
  summary,
  showSummaryCards = false,
  // Figma keeps the row number on Validation Result but drops it on Import
  // Details, so the two consumers of this table ask for it separately.
  showRowNumber = true,
  onError,
  footer = null,
  // Change this to force a refetch — the detail page passes the batch status so
  // the rows are re-read when a batch finishes validating or processing.
  refreshKey = null,
}) => {
  const [activeTab, setActiveTab] = useState('all');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  // Only the newest request is allowed to write state, so quick tab switching
  // cannot leave an older response on screen.
  const requestIdRef = useRef(0);
  const [pagination, setPagination] = useState({
    page: 1,
    pageCount: 1,
    size: PAGE_SIZE,
    total: 0,
  });

  const totalRows = summary?.totalRows || 0;
  const validRows = summary?.validRows || 0;
  const invalidRows = summary?.invalidRows || 0;

  const tabTotals = {
    all: totalRows,
    valid: validRows,
    invalid: invalidRows,
  };

  const fetchRows = useCallback(
    async (result, page) => {
      if (!batchId) return;

      requestIdRef.current += 1;
      const requestId = requestIdRef.current;

      setLoading(true);
      try {
        const data = await shopperPayoutService.getRows(batchId, {
          result,
          page,
          size: PAGE_SIZE,
        });

        if (requestId !== requestIdRef.current) return;

        setRows(data.data || []);
        setPagination({
          page: data.page || 1,
          pageCount: data.pageCount || 1,
          size: data.size || PAGE_SIZE,
          total: data.total || 0,
        });
      } catch (error) {
        if (requestId !== requestIdRef.current) return;

        setRows([]);
        setPagination(prev => ({ ...prev, pageCount: 1, total: 0 }));
        if (onError) {
          onError(
            shopperPayoutService.getErrorMessage(
              error,
              'Failed to load the payout rows.'
            )
          );
        }
      } finally {
        if (requestId === requestIdRef.current) setLoading(false);
      }
    },
    [batchId, onError]
  );

  useEffect(() => {
    fetchRows(activeTab, 1);
  }, [fetchRows, activeTab, refreshKey]);

  const handleTabChange = useCallback(tabKey => {
    setActiveTab(tabKey);
    setPagination(prev => ({ ...prev, page: 1 }));
  }, []);

  const handlePageChange = useCallback(
    page => {
      fetchRows(activeTab, page);
    },
    [fetchRows, activeTab]
  );

  const showingFrom =
    pagination.total === 0 ? 0 : (pagination.page - 1) * pagination.size + 1;
  const showingTo = Math.min(
    pagination.page * pagination.size,
    pagination.total
  );

  const columnCount = showRowNumber ? 6 : 5;

  return (
    <div className='bg-white rounded-lg shadow-sm border border-gray-200'>
      <div className='px-4 md:px-6 pt-5'>
        <h3 className='text-lg font-semibold text-gray-900'>
          Shopper Payout Result
        </h3>

        {showSummaryCards && (
          <div className='grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4'>
            <div className='border border-gray-200 rounded-lg px-4 py-3'>
              <p className='text-sm text-gray-500'>Total Shopper Payout</p>
              <p className='text-2xl font-bold text-gray-900 mt-1'>
                {shopperPayoutService.formatCount(totalRows)}
              </p>
            </div>
            <div className='bg-green-50 rounded-lg px-4 py-3'>
              <p className='text-sm text-green-700'>Valid</p>
              <p className='text-2xl font-bold text-green-700 mt-1'>
                {shopperPayoutService.formatCount(validRows)}
              </p>
            </div>
            <div className='bg-red-50 rounded-lg px-4 py-3'>
              <p className='text-sm text-red-700'>Invalid</p>
              <p className='text-2xl font-bold text-red-700 mt-1'>
                {shopperPayoutService.formatCount(invalidRows)}
              </p>
            </div>
          </div>
        )}

        <div className='flex gap-6 border-b border-gray-200 mt-5'>
          {TABS.map(tab => {
            const isActive = tab.key === activeTab;
            return (
              <button
                key={tab.key}
                type='button'
                onClick={() => handleTabChange(tab.key)}
                disabled={loading}
                className={`pb-3 text-sm font-medium border-b-2 transition-colors duration-150 focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed ${
                  isActive
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label} (
                {shopperPayoutService.formatCount(tabTotals[tab.key])})
              </button>
            );
          })}
        </div>
      </div>

      <div className='px-4 md:px-6 py-3 flex items-center gap-2'>
        <p className='text-sm text-gray-500'>
          Showing {shopperPayoutService.formatCount(showingFrom)} to{' '}
          {shopperPayoutService.formatCount(showingTo)} of{' '}
          {shopperPayoutService.formatCount(pagination.total)} Shopper payout
        </p>
        {loading && (
          <svg
            className='animate-spin h-4 w-4 text-blue-600'
            fill='none'
            viewBox='0 0 24 24'
          >
            <circle
              className='opacity-25'
              cx='12'
              cy='12'
              r='10'
              stroke='currentColor'
              strokeWidth='4'
            />
            <path
              className='opacity-75'
              fill='currentColor'
              d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
            />
          </svg>
        )}
      </div>

      <div className='overflow-x-auto'>
        <table className='min-w-full divide-y divide-gray-200'>
          <thead className='bg-gray-50'>
            <tr>
              {showRowNumber && (
                <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Row
                </th>
              )}
              <th className='px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                Shopper Name
              </th>
              <th className='px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                Shopper ID
              </th>
              <th className='px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider'>
                Amount (€)
              </th>
              <th className='px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                Result
              </th>
              <th className='px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                Reason
              </th>
            </tr>
          </thead>
          <tbody className='bg-white divide-y divide-gray-200'>
            {rows.length === 0 && !loading ? (
              <tr>
                <td
                  colSpan={columnCount}
                  className='px-6 py-8 text-center text-gray-500'
                >
                  <p className='text-lg font-medium'>No payout rows found</p>
                  <p className='text-sm'>
                    There is nothing to show for this filter.
                  </p>
                </td>
              </tr>
            ) : (
              rows.map(row => (
                <tr key={row.rowNumber} className='hover:bg-gray-50'>
                  {showRowNumber && (
                    <td className='px-4 py-4 whitespace-nowrap text-sm text-gray-500'>
                      {row.rowNumber}
                    </td>
                  )}
                  <td className='px-3 py-4 whitespace-nowrap text-sm text-gray-900'>
                    {row.shopperName || '-'}
                  </td>
                  <td className='px-3 py-4 whitespace-nowrap text-sm text-gray-500 font-mono'>
                    {row.shopperId || '-'}
                  </td>
                  <td className='px-3 py-4 whitespace-nowrap text-sm text-gray-900 text-right'>
                    {row.amount || '-'}
                  </td>
                  <td className='px-3 py-4 whitespace-nowrap'>
                    <StatusBadge value={row.result} kind='result' />
                  </td>
                  <td className='px-3 py-4 text-sm text-gray-700'>
                    {row.reason || '-'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Pagination
        page={pagination.page}
        pageCount={pagination.pageCount}
        onPageChange={handlePageChange}
        disabled={loading}
      />

      {footer && (
        <div className='px-4 md:px-6 py-4 border-t border-gray-200'>
          {footer}
        </div>
      )}
    </div>
  );
};

export default PayoutRowsTable;
